import { useEffect, useMemo, useRef, useState } from "react";
import { Save, Eye, EyeOff, ShieldCheck, User as UserIcon, Phone, Cake, Award, Camera, Trash2, X, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { EditAvatarDialog } from "@/components/EditAvatarDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { formatAnniversary } from "@/lib/celebrations";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/**
 * Iter 103 — Self-service Profile page.
 *
 * Any authenticated user can edit their own name, phone, birthday and
 * password from here. Password changes require the current password
 * AND obey the server-side policy (mirrored from
 * GET /api/auth/password-policy). The strength meter shows the same
 * rules the backend will enforce, so users never hit a surprise 400.
 */
export default function Profile() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    b_month: "none",
    b_day: "",
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [policy, setPolicy] = useState(null);

  // Iter 103 — Avatar upload. Accepts a local file, downsizes to 256px
  // via a canvas so we never blow past the backend's 500KB data-URL
  // cap, and PUTs to /api/me/avatar (existing endpoint).
  const [avatarUrl, setAvatarUrl] = useState("");
  const [avatarSaving, setAvatarSaving] = useState(false);
  const [avatarPickerOpen, setAvatarPickerOpen] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/auth/password-policy").then((r) => setPolicy(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    const [bm, bd] = (user.birthday || "").split("-");
    setForm((f) => ({
      ...f,
      name: user.name || "",
      phone: user.phone || "",
      b_month: bm ? String(parseInt(bm, 10)) : "none",
      b_day:   bd ? String(parseInt(bd, 10)) : "",
    }));
    setAvatarUrl(user.avatar_url || "");
  }, [user?.user_id]);

  const strength = useMemo(() => scorePassword(form.new_password, policy), [form.new_password, policy]);
  const wantsPwChange = !!(form.new_password || form.confirm_password);
  const pwMismatch = wantsPwChange && form.new_password !== form.confirm_password;


  // Iter 103 — Avatar handlers. Downsize to 256px via canvas so we stay
  // well under the backend 500KB data-URL cap even for high-res phone
  // photos, then PUT to the existing /me/avatar endpoint.
  const downsizeToDataUrl = (file) => new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => { img.src = reader.result; };
    reader.onerror = () => reject(new Error("Could not read file"));
    img.onload = () => {
      const MAX = 256;
      const ratio = Math.min(1, MAX / Math.max(img.width, img.height));
      const w = Math.round(img.width * ratio);
      const h = Math.round(img.height * ratio);
      const c = document.createElement("canvas");
      c.width = w; c.height = h;
      c.getContext("2d").drawImage(img, 0, 0, w, h);
      resolve(c.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = () => reject(new Error("Not an image"));
    reader.readAsDataURL(file);
  });

  const onAvatarPicked = async (e) => {
    const f = e?.target?.files?.[0];
    if (!f) return;
    if (!/^image\//.test(f.type)) { toast.error("Please choose an image file."); return; }
    setAvatarSaving(true);
    try {
      const dataUrl = await downsizeToDataUrl(f);
      const { data } = await api.put("/me/avatar", { avatar_url: dataUrl });
      setAvatarUrl(data.avatar_url || "");
      toast.success("Avatar updated");
      if (typeof checkAuth === "function") await checkAuth();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err?.message || "Avatar update failed");
    } finally {
      setAvatarSaving(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeAvatar = async () => {
    if (avatarSaving) return;
    setAvatarSaving(true);
    try {
      await api.put("/me/avatar", { avatar_url: "" });
      setAvatarUrl("");
      toast.success("Avatar removed");
      if (typeof checkAuth === "function") await checkAuth();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Remove failed");
    } finally {
      setAvatarSaving(false);
    }
  };


  const save = async () => {
    if (saving) return;
    if (!form.name.trim()) {
      toast.error("Name cannot be blank.");
      return;
    }
    if (wantsPwChange) {
      if (!form.current_password) {
        toast.error("Enter your current password to change it.");
        return;
      }
      if (pwMismatch) {
        toast.error("New passwords don't match.");
        return;
      }
      if (policy && !strength.meetsPolicy) {
        toast.error(strength.hint || "New password doesn't meet the strength policy.");
        return;
      }
    }
    const birthday =
      form.b_month !== "none" && form.b_day
        ? `${String(form.b_month).padStart(2, "0")}-${String(form.b_day).padStart(2, "0")}`
        : "";
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        birthday,
      };
      if (wantsPwChange) {
        payload.current_password = form.current_password;
        payload.new_password = form.new_password;
      }
      await api.patch("/auth/profile", payload);
      toast.success("Profile updated");
      // Clear password fields on success
      setForm((f) => ({ ...f, current_password: "", new_password: "", confirm_password: "" }));
      if (typeof checkAuth === "function") await checkAuth();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl" data-testid="profile-page">
      <PageHeader testid="profile-header" title="Profile" subtitle="Update your personal info and password." />

      <Card className="p-6">
        <div className="space-y-6">
          {/* Iter 103 — Avatar picker. Displays current avatar (or
              initials fallback) plus buttons to upload/replace and
              remove. Uploads are auto-downsized to 256px so the
              per-user doc stays small. */}
          <div className="flex items-center gap-5" data-testid="profile-avatar-row">
            <div className="relative">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt="Your avatar"
                  className="h-20 w-20 rounded-full object-cover ring-2 ring-slate-200"
                  data-testid="profile-avatar-img"
                />
              ) : (
                <div
                  className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 text-2xl font-semibold text-primary ring-2 ring-slate-200"
                  data-testid="profile-avatar-initials">
                  {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
                </div>
              )}
              {avatarSaving && (
                <div className="absolute inset-0 flex items-center justify-center rounded-full bg-white/60 text-xs">Saving…</div>
              )}
            </div>
            <div className="space-y-1.5">
              <div className="text-sm font-semibold text-slate-700">Profile picture</div>
              <div className="text-xs text-slate-500">Pick a themed cartoon face, or upload your own (JPG/PNG, auto-resized to 256px).</div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setAvatarPickerOpen(true)}
                  disabled={avatarSaving}
                  data-testid="profile-avatar-choose">
                  <Sparkles className="h-3.5 w-3.5" /> Choose avatar
                </Button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  onChange={onAvatarPicked}
                  className="hidden"
                  data-testid="profile-avatar-file-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileRef.current?.click()}
                  disabled={avatarSaving}
                  data-testid="profile-avatar-upload">
                  <Camera className="h-3.5 w-3.5" />
                  {avatarUrl ? "Replace" : "Upload"}
                </Button>
                {avatarUrl && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={removeAvatar}
                    disabled={avatarSaving}
                    data-testid="profile-avatar-remove">
                    <Trash2 className="h-3.5 w-3.5" /> Remove
                  </Button>
                )}
              </div>
            </div>
          </div>

          <EditAvatarDialog open={avatarPickerOpen} onOpenChange={setAvatarPickerOpen}
            user={{ ...(user || {}), avatar_url: avatarUrl }}
            onSaved={(url) => { setAvatarUrl(url || ""); checkAuth?.(); }} />

          {/* Name */}
          <div className="space-y-1.5">
            <Label htmlFor="profile-name" className="flex items-center gap-1.5"><UserIcon className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500" /> Full name</Label>
            <Input
              id="profile-name"
              data-testid="profile-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              disabled={saving}
              maxLength={120}
              placeholder="Your name"
            />
          </div>

          {/* Phone */}
          <div className="space-y-1.5">
            <Label htmlFor="profile-phone" className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500" /> Phone</Label>
            <Input
              id="profile-phone"
              data-testid="profile-phone-input"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              disabled={saving}
              maxLength={40}
              placeholder="+1 555 555 5555"
            />
          </div>

          {/* Birthday */}
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5"><Cake className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500" /> Birthday <span className="text-xs font-normal text-slate-400 dark:text-slate-500">(month &amp; day)</span></Label>
            <div className="grid grid-cols-2 gap-2">
              <Select value={form.b_month} onValueChange={(v) => setForm({ ...form, b_month: v })}>
                <SelectTrigger data-testid="profile-birthday-month"><SelectValue placeholder="Month" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {MONTHS.map((m, i) => (
                    <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                data-testid="profile-birthday-day"
                value={form.b_day}
                onChange={(e) => setForm({ ...form, b_day: e.target.value.replace(/[^\d]/g, "").slice(0, 2) })}
                placeholder="Day"
                inputMode="numeric"
                disabled={saving || form.b_month === "none"}
              />
            </div>
          </div>

          {/* Work anniversary — read-only; HR sets it in Settings → Staff. */}
          {(() => {
            const a = formatAnniversary(user?.work_anniversary);
            if (!a) return null;
            return (
              <div className="space-y-1.5" data-testid="profile-work-anniversary">
                <Label className="flex items-center gap-1.5"><Award className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500" /> Work anniversary</Label>
                <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-200">
                  {a.dateLabel}
                  <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                    {a.years} year{a.years === 1 ? "" : "s"}
                  </span>
                  <span className="ml-auto text-xs text-slate-400">Set by HR</span>
                </div>
              </div>
            );
          })()}

          {/* Password change block */}
          <div className="rounded-lg border border-slate-200 bg-slate-50/60 dark:border-slate-700 dark:bg-slate-800/40 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-700 dark:text-slate-100 flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4 text-primary" /> Change password
              </div>
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-300 hover:text-primary"
                data-testid="profile-toggle-pw-visibility">
                {showPw ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                {showPw ? "Hide" : "Show"}
              </button>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">Leave blank to keep your current password.</p>

            <div className="space-y-1.5">
              <Label htmlFor="profile-current-pw" className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300">Current password</Label>
              <Input
                id="profile-current-pw"
                type={showPw ? "text" : "password"}
                autoComplete="current-password"
                value={form.current_password}
                onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                disabled={saving}
                data-testid="profile-current-pw-input"
                placeholder="Required only if changing password"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="profile-new-pw" className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300">New password</Label>
              <Input
                id="profile-new-pw"
                type={showPw ? "text" : "password"}
                autoComplete="new-password"
                value={form.new_password}
                onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                disabled={saving}
                data-testid="profile-new-pw-input"
              />
              {form.new_password && (
                <PasswordStrengthMeter strength={strength} />
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="profile-confirm-pw" className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300">Confirm new password</Label>
              <Input
                id="profile-confirm-pw"
                type={showPw ? "text" : "password"}
                autoComplete="new-password"
                value={form.confirm_password}
                onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                disabled={saving}
                data-testid="profile-confirm-pw-input"
                className={pwMismatch ? "border-red-300 focus-visible:ring-red-300" : ""}
              />
              {pwMismatch && (
                <p className="text-xs text-red-600" data-testid="profile-pw-mismatch">Passwords don&apos;t match.</p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              data-testid="profile-cancel-button">
              <X className="h-4 w-4" />
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={saving || pwMismatch || (wantsPwChange && policy && !strength.meetsPolicy)}
              data-testid="profile-save-button">
              <Save className="h-4 w-4" />
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}


/**
 * Iter 103 — Password strength scoring. Mirrors the server-side
 * policy so the UI never allows a submit the backend would 400.
 * Returns `{ score 0-4, label, hint, meetsPolicy }`.
 */
function scorePassword(pw, policy) {
  if (!pw) return { score: 0, label: "—", hint: "", meetsPolicy: false };
  const p = policy || { min_length: 10, require_upper: true, require_lower: true, require_digit: true, require_special: false };
  const hasUpper = /[A-Z]/.test(pw);
  const hasLower = /[a-z]/.test(pw);
  const hasDigit = /\d/.test(pw);
  const hasSpecial = /[^A-Za-z0-9]/.test(pw);
  const longEnough = pw.length >= p.min_length;

  const violations = [];
  if (!longEnough) violations.push(`at least ${p.min_length} characters`);
  if (p.require_upper && !hasUpper) violations.push("an uppercase letter");
  if (p.require_lower && !hasLower) violations.push("a lowercase letter");
  if (p.require_digit && !hasDigit) violations.push("a digit");
  if (p.require_special && !hasSpecial) violations.push("a special character");

  // Score by number of classes + length beyond minimum.
  let score = 0;
  if (hasUpper) score++;
  if (hasLower) score++;
  if (hasDigit) score++;
  if (hasSpecial) score++;
  if (pw.length >= p.min_length + 4) score++;
  score = Math.min(4, score);

  const labels = ["Very weak", "Weak", "Fair", "Strong", "Very strong"];
  const meetsPolicy = violations.length === 0;
  return {
    score,
    label: labels[score],
    hint: violations.length ? `Password needs ${violations.join(", ")}.` : "Meets policy.",
    meetsPolicy,
  };
}


function PasswordStrengthMeter({ strength }) {
  const barColors = ["bg-red-400", "bg-orange-400", "bg-amber-400", "bg-emerald-500", "bg-emerald-600"];
  return (
    <div className="space-y-1" data-testid="profile-pw-strength">
      <div className="flex gap-1">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={`h-1.5 flex-1 rounded ${i < strength.score ? barColors[strength.score] : "bg-slate-200"}`} />
        ))}
      </div>
      <div className="flex items-center justify-between text-[11px]">
        <span className={strength.meetsPolicy ? "text-emerald-700" : "text-amber-700"} data-testid="profile-pw-label">
          {strength.label}
        </span>
        <span className="text-slate-500">{strength.hint}</span>
      </div>
    </div>
  );
}
